import type { ReactNode } from "react";

/**
 * Canvas sheet: slides in from the right (bottom sheet on phones) with a title, optional sub-line
 * and a pinned footer; narrow ones (width under 440) stay a centred confirmation dialog.
 */
export function Modal({
  onClose,
  width = 440,
  title,
  sub,
  footer,
  children,
}: {
  onClose: () => void;
  width?: number;
  title: string;
  sub?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={`o-scrim ${width < 440 ? "o-dialog--center" : "o-dialog--side"}`} style={{ zIndex: 140 }} onClick={onClose}>
      <section className="o-dialog" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()} style={{ ["--w" as string]: `${Math.max(width, 500)}px` }}>
        <header className="o-dialog__head">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2>{title}</h2>
            {sub && <div className="o-row__sub" style={{ fontSize: 13, whiteSpace: "normal" }}>{sub}</div>}
          </div>
          <button type="button" aria-label="Close" className="o-close" onClick={onClose}>
            <span className="ms" aria-hidden="true">close</span>
          </button>
        </header>
        <div className="o-dialog__body">{children}</div>
        {footer && <footer className="o-dialog__foot">{footer}</footer>}
      </section>
    </div>
  );
}

export function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="o-field">
      <div className="o-field__label">{label}</div>
      {children}
    </div>
  );
}

/** Pick-one chips (the canvas's replacement for selects with a handful of options). */
/** "Are you sure?" as a centred dialog: a title, one line of what happens, Cancel and the action. */
export function ConfirmModal({ title, body, action, onClose, onConfirm }: { title: string; body: string; action: string; onClose: () => void; onConfirm: () => void }) {
  return (
    <Modal
      onClose={onClose}
      width={420}
      title={title}
      footer={
        <>
          <button type="button" onClick={onClose} className="o-btn o-btn--grey" style={{ flex: 1 }}>
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="o-btn o-btn--coral" style={{ flex: 1 }}>
            {action}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)", lineHeight: 1.55 }}>{body}</p>
    </Modal>
  );
}

export function Chips<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void }) {
  return (
    <Field label={label}>
      <div className="o-chips" role="radiogroup" aria-label={label} style={{ flexWrap: "wrap" }}>
        {options.map((o) => (
          <button type="button" role="radio" aria-checked={value === o.value} key={o.value} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </Field>
  );
}

export function SwitchRow({ label, note, checked, onChange }: { label: string; note?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="o-switch-row">
      <div>
        <strong>{label}</strong>
        {note && <span>{note}</span>}
      </div>
      <button type="button" role="switch" aria-checked={checked} aria-label={label} className="o-toggle" onClick={() => onChange(!checked)}>
        <span className="o-toggle__track" aria-hidden="true" />
      </button>
    </div>
  );
}
